/**
  @name SelectMenu
@class

  @author Christine Hager <chhager@deloitte.com>

  * @example <SelectMenu options={options} />

*/

// THIRD-PARTY
import * as React from "react";

import { isUndefined, memoize } from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";

// COMPONENTS

// STYLES
import "./select-menu.scss";

// UTILITIES
import {
  getDeviceBreakpoint,
  onNextFrame,
  getResizeSubscriber,
} from "../../lib/breakpoints";

class SelectMenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: this.props.default || "", // TODO: remove props from setting state. (antipattern)
      listVisible: false,
      currentBreakpoint: getDeviceBreakpoint(),
      selectDefault: true,
    };

    this.isCustomMenu = this.isCustomMenu.bind(this);
    this.showMenu = this.showMenu.bind(this);
    this.setWrapperRef = this.setWrapperRef.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.resizeSubscriber = getResizeSubscriber(this.handleResize);
    this.selectOption = this.selectOption.bind(this);
    this.addAvailabilityProp = this.addAvailabilityProp.bind(this);
    this.getOptionsArrMemoized = memoize(this.getOptionsArrMemoized);
    this.mobileSelectElementRef = React.createRef();
  }

  static getDerivedStateFromProps(nextProps) {
    const { updatedSizeFromQueryParam, updatedSelectMenuOptions } = nextProps;

    if (updatedSizeFromQueryParam?.isUpdateRequired === true) {
      updatedSelectMenuOptions();
      if (updatedSizeFromQueryParam?.updatedSize) {
        return {
          selected: updatedSizeFromQueryParam.updatedSize,
        };
      }

      return {
        selectDefault: true,
      };
    }
    return null;
  }

  componentDidMount() {
    onNextFrame(this.handleResize);
    document.addEventListener("mousedown", this.handleClickOutside);
  }

  componentDidUpdate(prevProps) {
    const { direction = "down", default: defaultProp } = this.props;

    if (prevProps.default !== defaultProp) {
      this.setState({
        selectDefault: true,
      });
    }

    if (this.customDropdown && direction === "up") {
      this.customDropdown.style.top = `-${
        this.customDropdown.offsetHeight + 8
      }px`;
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  getOptionsArrMemoized(options, enabledOptions) {
    const areOptions = !!options?.length;

    if (areOptions && enabledOptions) {
      return this.addAvailabilityProp(options, enabledOptions);
    }

    if (areOptions && !enabledOptions) {
      return options;
    }

    return [];
  }

  setWrapperRef(node) {
    this.wrapperRef = node;
  }

  isCustomMenu() {
    const { currentBreakpoint } = this.state;
    return (
      currentBreakpoint !== "small" &&
      currentBreakpoint !== "mobile" &&
      currentBreakpoint !== "tablet"
    );
  }

  selectOption(event, item, index) {
    if (this.props.onSelectOption) {
      this.props.onSelectOption(event, item, index);
    }
    if (typeof item === "undefined" && typeof event !== "undefined") {
      item = {
        value: event.target.value,
      };
    }

    // ENABLE CALLBACK
    // FIXME: work to make 'onSelectOption' the new 'callback'
    if (this.props.searchSelectMenu === true && typeof index === "undefined") {
      if (typeof this.props.callback === "function")
        this.props.callback(null, null, Number(event.target.value));
    } else if (typeof this.props.callback === "function")
      this.props.callback(item, this.context, index);

    if (this.isAvailable(item.value)) {
      this.setState({
        selected: item.text || item.value || item.label,
      });
    } else {
      this.setState({
        selected: item.text || item.value || item.label,
      });
    }
  }

  showMenu(event) {
    if (this.isCustomMenu()) {
      this.setState({
        listVisible: !this.state.listVisible,
      });
    }
  }

  handleClickOutside(event) {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState({
        listVisible: false,
      });
    }
  }

  handleResize() {
    this.setState({
      currentBreakpoint: getDeviceBreakpoint(),
      listVisible: false,
    });
  }

  addAvailabilityProp(options, enabledOptions) {
    const stateOptions = Object.assign([], options);
    stateOptions.forEach((obj) => {
      obj.isAvailable = true;
      if (!isUndefined(enabledOptions) && !enabledOptions.includes(obj.value)) {
        obj.isAvailable = false;
      }
    });
    return stateOptions;
  }

  isAvailable(val) {
    return !(
      !this.props.enabledOptions ||
      !this.props.enabledOptions.length ||
      !this.props.enabledOptions.includes(val)
    );
  }

  /**
    JSX to render the component
  */
  render() {
    const {
      className,
      subtitle: subtitleProp,
      searchSelectMenu,
      default: defaultOption,
      title,
      appendingText = "",
      enabledOptions,
      cdpSort,
      isBopisEnabled,
      ariaDescribedBy,
      ariaInvalid,
      options,
    } = this.props;

    const {
      currentBreakpoint: bp,
      selected,
      selectDefault,
      listVisible,
    } = this.state;

    const optionsArr = this.getOptionsArrMemoized(options, enabledOptions);

    let subtitle;
    let selectMenu;

    if (subtitleProp && selected === "") {
      subtitle = <span className="select-menu__selected">{subtitleProp}</span>;
    } else if (this.isCustomMenu() && selected !== "") {
      if (optionsArr.find((s) => s.label === selected)) {
        // if selected is in sort options
        subtitle = (
          <span className="select-menu__selected">
            {subtitleProp}
            {!this.isAvailable(selected) ? `${appendingText}` : ""}
          </span>
        );
      } else {
        subtitle = (
          <span className="select-menu__selected">{subtitleProp}</span>
        ); // show default sort
      }
    }

    let defaultItem = "";

    if (selectDefault) {
      if (searchSelectMenu === true || cdpSort) {
        defaultItem = (
          <option value="default" disabled>
            {defaultOption || "Select Sort Order"}
          </option>
        );
      } else {
        defaultItem = (
          <option value="default" disabled>
            {defaultOption || "Select Size"}
          </option>
        );
      }
    }

    if (bp === "small" || bp === "mobile" || bp === "tablet") {
      selectMenu = (
        <select
          id={title}
          onChange={this.selectOption}
          className="lll-text-primary select-menu__select"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          ref={this.mobileSelectElementRef}
          defaultValue="default"
        >
          {defaultItem}
          {optionsArr.map((item, index) => {
            let option;
            const optionText = item.text || item.value || item.label;
            const optionTextSuffix =
              item.isAvailable || cdpSort || searchSelectMenu
                ? ""
                : appendingText;
            const optionVal = item.value || item.id || index;

            if (cdpSort) {
              option = (
                <option
                  value={optionVal}
                  key={optionVal}
                  selected={defaultOption === optionText}
                  className={item.className || ""}
                >
                  {optionText}
                </option>
              );
            } else if (searchSelectMenu === true) {
              option = (
                <option
                  value={optionVal}
                  key={optionVal}
                  className={item.className || ""}
                >
                  {item.label}
                </option>
              );
            } else {
              option = (
                <option
                  value={optionVal}
                  key={optionVal}
                  className={item.className || ""}
                >
                  Size: {optionText}
                  {optionTextSuffix}
                </option>
              );
            }
            return option;
          })}
        </select>
      );
    } else {
      selectMenu = (
        <div
          className={`custom-dropdown${listVisible ? " show" : " hide"}`}
          aria-hidden="true"
          ref={(e) => (this.customDropdown = e)}
        >
          <span className="hidden">
            <select
              id={title}
              onChange={this.selectOption}
              className="select-menu__select"
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
            >
              {defaultItem}
              {optionsArr.map((item, index) => {
                const optionText = item.text || item.value || item.label;
                const optionVal = item.value || item.id || index;
                const optionDataID = optionText
                  .split(" ")
                  .join("")
                  .replace(":", "");

                return (
                  <option
                    data-testid={`${optionDataID}-option`}
                    value={optionVal}
                    key={optionVal}
                    className={item.className || ""}
                  >
                    {optionText}
                  </option>
                );
              })}
            </select>
          </span>
          <ul id={`custom${title}`}>
            <li value={defaultOption} />
            {optionsArr.map((item, index) => {
              const optionText = item.text || item.value || item.label;
              const optionTextSuffix =
                item.isAvailable || cdpSort || searchSelectMenu
                  ? ""
                  : appendingText;
              const optionVal = item.value || item.id || index;

              return (
                <li key={optionVal}>
                  <div
                    onClick={(e) => {
                      this.selectOption(e, item, index);
                    }}
                  >
                    {optionText}
                    {optionTextSuffix}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    return (
      <div className="select-menu" ref={this.setWrapperRef}>
        <div
          className={classNames({
            container: true,
            "bopis-enabled": isBopisEnabled,
            [className]: !!className,
          })}
          onClick={(event) => this.showMenu(event)}
        >
          <label className="select-menu__label" htmlFor={title}>
            {title}
          </label>
          {subtitle}
          {selectMenu}
        </div>
      </div>
    );
  }
}

SelectMenu.defaultProps = {
  ariaDescribedBy: undefined,
  ariaInvalid: undefined,
  enabledOptions: undefined,
  updatedSizeFromQueryParam: undefined,
};

SelectMenu.propTypes = {
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  enabledOptions: PropTypes.arrayOf(PropTypes.string),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      text: PropTypes.string,
    })
  ),
  default: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  searchSelectMenu: PropTypes.bool,
  updatedSizeFromQueryParam: PropTypes.shape({
    isUpdateRequired: PropTypes.bool,
    updatedSize: PropTypes.string,
  }),
  updatedSelectMenuOptions: PropTypes.func,
  direction: PropTypes.string,
};

export default SelectMenu;
